import styled from 'styled-components';
import { useSelector } from 'src/store/store';
import i18n from 'src/utils/translate';
import { getCommunityLogoUrl } from 'src/utils/linkUtils';
import Button from 'src/components/buttons/button';
import FollowButton from 'src/components/buttons/followButton';
import WriteButton from 'src/modules/community/shared/components/writeButton';
import { Element, Section } from 'src/constants/footfall';
import { userIsMemberOfCurrentCommunity, userHasRole } from 'src/utils/context';
import { hideWriteCTA } from 'src/constants/restrictedCommunities';
import ImageUpload from 'src/components/imageUpload';
import { saveAndCropLogoImage } from 'src/api/community';
import AdminActions from 'src/modules/community/shared/components/header/adminActions';
import { helpScoutArticle } from 'src/utils/helpScoutBeacon';
import Separator from 'src/components/separator';
import { Role } from 'src/constants/types';

type Props = {
  currentPage?: string;
}
export default function CommunityHeaderContent({ currentPage }: Props): JSX.Element {
  const { currentCommunity, currentUser } = useSelector(({ context }) => context);

  const imageUrl = getCommunityLogoUrl(currentCommunity.logoImageHash);
  const logoStyle = {
    backgroundImage: `url("${imageUrl}")`,
  };
  const isMemberOfCommunity = userIsMemberOfCurrentCommunity(currentUser, currentCommunity);
  const isHUStaff = userHasRole(currentUser, [Role.HealthUnlocked]);
  const isArchivedCommunityAndHUStaff = currentCommunity?.isArchived && isHUStaff;

  return (
    <div className="community-header__content">
      <div className="community-header__desc" data-testid="community-header-desc">
        <div className="community-header__logo">
          {isHUStaff ? (
            <ImageUpload
              boundary={{ width: 120, height: 120 }}
              buttonText={i18n.t('Add logo')}
              className="community-header__logo-img"
              communitySlug={currentCommunity.slug}
              currentImageUrl={imageUrl}
              fitBest={true}
              key={imageUrl} // refreshes class component on change
              onSave={saveAndCropLogoImage}
            />
          ) : (
            <div className="community-header__logo-img" style={logoStyle} />
          )}
        </div>
        <div className="community-header__desc-content">
          <h2 className="community-header__title">{currentCommunity.name}</h2>
          {(currentCommunity?.isArchived) ? (
            <div className="community-header__subtitle">
              <span>{currentCommunity?.postCount?.toLocaleString()} {i18n.t('posts')}</span>
              <Separator />
              <ArchivedButton
                icon="information"
                iconLeftAligned={false}
                type="link"
                onClick={() => helpScoutArticle('172-what-are-archived-communities')}
              >
                {i18n.t('Archived')}
              </ArchivedButton>
            </div>
          ) : (currentUser || currentCommunity?.followerCount >= 300) && (
            <div className="community-header__subtitle">
              {currentCommunity?.followerCount?.toLocaleString()} {i18n.t('members')}
              <Separator />
              {currentCommunity?.postCount?.toLocaleString()} {i18n.t('posts')}
            </div>
          )}
        </div>
        {(isArchivedCommunityAndHUStaff || !currentCommunity.isArchived) && (
          <div className="community-header__desc-actions">
            {isHUStaff
              ? <AdminActions />
              : (
                <FollowButton
                  community={currentCommunity}
                  followType="community"
                  isFollowing={isMemberOfCommunity}
                  signupContext={{
                    element: Element.FOLLOW,
                    section: ('community-' + currentPage) as Section,
                  }}
                />
              )}
            {!hideWriteCTA.includes(currentCommunity?.slug) && (
              <WriteButton trackingSection={'community-' + currentPage as Section}>
                {i18n.t('Write')}
              </WriteButton>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const ArchivedButton = styled(Button)`
  margin: 0;
  color: ${props => props.theme.colorTextLight};
  .icon {
    color: ${props => props.theme.colorButtonPrimary};
    &:before {
      -webkit-font-smoothing: auto;
      -moz-osx-font-smoothing: auto;
    }
  }
`;
